<script lang="ts" setup>
import Logo from '@/components/Logo.vue'
import MainNav from '@/components/MainNav.vue'
import MiniNav from '@/components/MiniNav.vue'
import SubNavigation from '@/components/navigation/SubNavigation.vue'
import BottomNavigation from '@/components/navigation/BottomNavigation.vue'
import AppBarNavigation from '@/components/navigation/AppBarNavigation.vue'

const route = useRoute()
const layoutMeta = computed(
  () =>
    ({
      subNavType: null,
      contentWidth: 'contained',
      ...route.meta.main,
    }) satisfies CustomPageMeta['main']
)

const { settings } = storeToRefs(useBusinessStore())

const companyLogo = computed(() => settings.value.companyBranding?.logo.url)

const showDrawer = ref(false)

const { xs, smAndDown, smAndUp, mdAndUp } = useDisplay()

const isBottomNav = computed(() => smAndDown.value)

const appBarHeight = computed(() => (smAndUp.value ? 80 : 70))

const subNavType = computed(() => layoutMeta.value.subNavType)

const contentWidth = computed(() => {
  if (layoutMeta.value.contentWidth === 'full') {
    return '100%'
  }
  return subNavType.value ? '830px' : '1200px'
})

const { navigationItems } = useSubNav(subNavType)
</script>

<template>
  <v-main>
    <v-app-bar fixed flat order="1" :height="appBarHeight" class="app-bar px-8">
      <div class="w-100 d-flex flex-column justify-center h-100 app-bar__content">
        <div class="w-100 d-flex flex-row align-center justify-space-between">
          <div v-if="smAndDown" class="menu-toggle">
            <v-app-bar-nav-icon :ripple="false" @click="showDrawer = !showDrawer">
              <v-icon size="x-large">custom:menu</v-icon>
            </v-app-bar-nav-icon>
          </div>
          <slot name="appbarContent">
            <logo data-cy="indexLogo" class="sh__logo" :image="companyLogo" />
          </slot>
          <slot name="appbarNavigation">
            <app-bar-navigation v-if="mdAndUp" class="d-flex flex-row justify-end" />
          </slot>
        </div>
      </div>
    </v-app-bar>
    <div v-if="$slots.filterbar" class="px-8">
      <slot name="filterbar" />
    </div>
    <div v-if="$slots.banner" class="banner">
      <slot name="banner" />
    </div>
    <main-nav order="0" :active="showDrawer" @change="showDrawer = $event" />
    <mini-nav v-if="mdAndUp" order="0" @change="showDrawer = !showDrawer" />
    <v-container fluid class="pa-0 px-8">
      <v-row :no-gutters="xs">
        <v-col v-if="!isBottomNav && navigationItems.length" class="flex-grow-0">
          <div class="sub-nav pt-7">
            <slot name="subNav">
              <sub-navigation :items="navigationItems" />
            </slot>
          </div>
        </v-col>
        <v-col>
          <div class="content">
            <slot />
          </div>
        </v-col>
      </v-row>
    </v-container>
    <slot name="widgets" />
    <slot name="bottomNav">
      <bottom-navigation v-if="isBottomNav && navigationItems.length" order="-1" :items="navigationItems" />
    </slot>
  </v-main>
</template>

<style lang="scss" scoped>
.app-bar {
  background-color: $c-white;

  .menu-toggle {
    margin-right: 4 * $bw;
    &:hover {
      color: $c-primary;
    }
  }

  &__content {
    border-bottom: solid thin rgba($c-grey-dark, 0.12);
  }

  :deep(.v-toolbar__content) {
    overflow: visible;
  }
}

.banner {
  position: sticky;
  top: $appbar-height;
  z-index: 10;
  padding: 0 8 * $bw;
}

.sub-nav {
  position: sticky;
  top: $appbar-height;
}

.content {
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: v-bind(contentWidth);
  margin: 0 auto;
}
</style>
